import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, Calendar, GraduationCap,
  BookOpen, AtSign, Loader2,
} from 'lucide-react';
import { fetchStudentById } from '../services/api';

const SYLLABUS_COLORS = {
  Local:     'bg-green-500/10 text-green-400 border-green-500/20',
  Cambridge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Edexcel:   'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const InfoCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-2xl">
    <div className="p-2.5 rounded-xl bg-accentBlue/10 text-accentBlue shrink-0">
      <Icon size={18} />
    </div>
    <div>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
      <p className="font-semibold text-white">{value || '—'}</p>
    </div>
  </div>
);

const StudentProfilePage = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(() => JSON.parse(localStorage.getItem('userInfo') || '{}'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!student._id || student.role !== 'student') {
      navigate('/login');
      return;
    }
    fetchStudentById(student._id)
      .then((res) => {
        const updated = { ...student, ...res.data };
        setStudent(updated);
        localStorage.setItem('userInfo', JSON.stringify(updated));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student._id]);

  const initials = `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`.toUpperCase() || 'S';
  const subjectList = student.subjects || [];
  const syllabusList = student.syllabus || [];

  return (
    <div className="min-h-screen bg-darkBg text-white">
      {loading ? (
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 size={36} className="animate-spin text-accentBlue opacity-60" />
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-6 py-12 space-y-6">

          {/* Hero card */}
          <div className="bg-gradient-to-r from-accentBlue/20 to-accentBlue/5 border border-accentBlue/20 rounded-3xl p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-accentBlue/20 text-accentBlue flex items-center justify-center text-4xl font-bold shrink-0 border border-accentBlue/30">
              {initials}
            </div>
            <div className="text-center sm:text-left">
              <h1 className="text-3xl font-bold text-white mb-1">
                {student.firstName} {student.lastName}
              </h1>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2">
                <span className="px-3 py-1 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-xs font-bold rounded-full uppercase tracking-wider">
                  Student
                </span>
                {student.level && (
                  <span className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-bold rounded-full">
                    {student.level}
                  </span>
                )}
                {syllabusList.map(sy => (
                  <span key={sy} className={`px-3 py-1 border text-xs font-bold rounded-full ${SYLLABUS_COLORS[sy] || 'bg-white/10 text-gray-300 border-white/20'}`}>
                    {sy}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Personal details */}
          <div>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Personal Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoCard icon={User}          label="Full Name"  value={`${student.firstName || ''} ${student.lastName || ''}`.trim()} />
              <InfoCard icon={AtSign}        label="Username"   value={student.username} />
              <InfoCard icon={Mail}          label="Email"      value={student.email} />
              <InfoCard icon={Phone}         label="Phone"      value={student.phone} />
              <InfoCard icon={Calendar}      label="Age"        value={student.age != null ? `${student.age} years` : null} />
              <InfoCard icon={GraduationCap} label="Level"      value={student.level} />
            </div>
          </div>

          {/* Syllabus */}
          <div>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Syllabus</h2>
            <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
              {syllabusList.length === 0 ? (
                <p className="text-gray-500 text-sm">No syllabus assigned.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {syllabusList.map(sy => (
                    <span key={sy} className={`px-4 py-2 border rounded-xl text-sm font-semibold ${SYLLABUS_COLORS[sy] || 'bg-white/10 text-gray-300 border-white/20'}`}>
                      {sy}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subjects */}
          <div>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 px-1">Subjects</h2>
            <div className="p-5 bg-white/5 border border-white/10 rounded-2xl">
              {subjectList.length === 0 ? (
                <p className="text-gray-500 text-sm">No subjects assigned yet.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {subjectList.map((s, i) => (
                    <span key={i} className="flex items-center gap-2 px-4 py-2 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue rounded-xl text-sm font-semibold">
                      <BookOpen size={14} />{s.subjectName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default StudentProfilePage;
